import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, User, Car, FileText, Loader2, Edit, Receipt, Eye, ShoppingCart } from 'lucide-react';
import { DocumentsSection } from '@/components/DocumentsSection';
import { CUILookupInput } from '@/components/CUILookupInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const statusLabels = { pending: 'In asteptare', in_processing: 'In procesare', completed: 'Finalizat', rejected: 'Respins', cancelled: 'Anulat' };
const invStatusLabels = { unpaid: 'Neplatita', partially_paid: 'Partial', paid: 'Platita', overdue: 'Scadenta', cancelled: 'Anulata' };
const invStatusColors = { unpaid: 'bg-amber-50 text-amber-700', paid: 'bg-emerald-50 text-emerald-700', cancelled: 'bg-slate-100 text-slate-500', overdue: 'bg-red-50 text-red-700', partially_paid: 'bg-blue-50 text-blue-700' };
const orderStatusColors = { pending: 'bg-amber-50 text-amber-700', in_processing: 'bg-blue-50 text-blue-700', completed: 'bg-emerald-50 text-emerald-700', rejected: 'bg-red-50 text-red-700' };

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_type: 'persoana_fizica', name: '', cnp_cui: '', id_document: '', administrator: '', email: '', phone: '', address: '', notes: '' });

  useEffect(() => { fetchClient(); fetchServices(); }, [clientId]);

  const fetchClient = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/clients/' + clientId, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      setClient(response.data);
    } catch (error) { toast.error('Eroare'); } finally { setLoading(false); }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(API + '/services');
      setServices(response.data);
    } catch (error) {}
  };

  const servicesMap = {};
  services.forEach(s => { servicesMap[s.service_id] = s.name; });

  const openEditDialog = () => {
    setForm({ client_type: client.client_type, name: client.name, cnp_cui: client.cnp_cui, id_document: client.id_document || '', administrator: client.administrator || '', email: client.email, phone: client.phone, address: client.address || '', notes: client.notes || '' });
    setEditDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.cnp_cui) { toast.error('Completati campurile obligatorii'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(API + '/admin/clients/' + clientId, form, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      toast.success('Client actualizat');
      setEditDialogOpen(false);
      fetchClient();
    } catch (error) { toast.error(error.response?.data?.detail || 'Eroare'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  if (!client) return <div className="text-center py-12">Client negasit</div>;

  // Compute stats
  const vehicles = client.vehicles || [];
  const orders = client.orders || [];
  const invoices = client.invoices || [];

  const allServicesUsed = [];
  orders.forEach(o => {
    (o.vehicles || []).forEach(v => {
      (v.services || []).forEach(s => {
        const sid = typeof s === 'string' ? s : s.service_id;
        const name = servicesMap[sid] || sid;
        if (!allServicesUsed.includes(name)) allServicesUsed.push(name);
      });
    });
  });

  const invPaid = invoices.filter(i => i.status === 'paid').length;
  const invUnpaid = invoices.filter(i => i.status === 'unpaid' || i.status === 'overdue').length;
  const invCancelled = invoices.filter(i => i.status === 'cancelled').length;
  const totalEur = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/clients"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Inapoi</Button></Link>
          <h1 className="text-2xl font-semibold text-slate-900">{client.name}</h1>
          <Badge variant="secondary">{client.client_type === 'persoana_juridica' ? 'PJ' : 'PF'}</Badge>
        </div>
        <Button onClick={openEditDialog} className="bg-blue-600 hover:bg-blue-700 text-white"><Edit className="h-4 w-4 mr-2" />Editeaza</Button>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editeaza client</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Tip client</Label><Select value={form.client_type} onValueChange={(v) => setForm({...form, client_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="persoana_fizica">Persoana fizica</SelectItem><SelectItem value="persoana_juridica">Persoana juridica</SelectItem></SelectContent></Select></div>
            <div><Label>{form.client_type === 'persoana_fizica' ? 'Nume complet' : 'Denumire firma'} *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
            <div><Label>{form.client_type === 'persoana_fizica' ? 'CNP' : 'CUI'} *</Label>
              {form.client_type === 'persoana_juridica' ? (
                <CUILookupInput value={form.cnp_cui} onChange={(e) => setForm({...form, cnp_cui: e.target.value})} onCompanyFound={(c) => setForm(prev => ({ ...prev, name: c.name || prev.name, cnp_cui: c.cui || prev.cnp_cui, address: c.address || prev.address, id_document: c.registration_number || prev.id_document }))} />
              ) : (<Input value={form.cnp_cui} onChange={(e) => setForm({...form, cnp_cui: e.target.value})} />)}
            </div>
            <div><Label>{form.client_type === 'persoana_fizica' ? 'Serie Numar CI' : 'Nr. Reg. Comertului'}</Label><Input value={form.id_document} onChange={(e) => setForm({...form, id_document: e.target.value})} /></div>
            {form.client_type === 'persoana_juridica' && (
              <div><Label>Administrator</Label><Input value={form.administrator} onChange={(e) => setForm({...form, administrator: e.target.value})} placeholder="Nume administrator" /></div>
            )}
            <div className="grid grid-cols-2 gap-4"><div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div><div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div></div>
            <div><Label>Adresa</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Notite</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Anuleaza</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Se salveaza...' : 'Salveaza'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Info + Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-white lg:col-span-2">
          <CardHeader><CardTitle className="flex items-center gap-2"><User className="h-5 w-5 text-blue-600" />Informatii</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div><p className="text-sm text-slate-500">Email</p><p>{client.email || 'N/A'}</p></div>
            <div><p className="text-sm text-slate-500">Telefon</p><p>{client.phone || 'N/A'}</p></div>
            <div><p className="text-sm text-slate-500">CNP/CUI</p><p>{client.cnp_cui}</p></div>
            {client.id_document && <div><p className="text-sm text-slate-500">{client.client_type === 'persoana_fizica' ? 'Serie Numar CI' : 'Nr. Reg. Comertului'}</p><p>{client.id_document}</p></div>}
            {client.client_type === 'persoana_juridica' && client.administrator && <div><p className="text-sm text-slate-500">Administrator</p><p>{client.administrator}</p></div>}
            <div className="col-span-2"><p className="text-sm text-slate-500">Adresa</p><p>{client.address || 'N/A'}</p></div>
            {client.notes && <div className="col-span-2"><p className="text-sm text-slate-500">Notite</p><p className="text-sm">{client.notes}</p></div>}
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardHeader><CardTitle>Sumar</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between"><span className="text-slate-500">Vehicule</span><span className="font-bold text-lg">{vehicles.length}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Comenzi</span><span className="font-bold text-lg">{orders.length}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Facturi platite</span><span className="font-bold text-lg text-emerald-600">{invPaid}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Facturi neplatite</span><span className="font-bold text-lg text-amber-600">{invUnpaid}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Facturi anulate</span><span className="font-bold text-lg text-slate-400">{invCancelled}</span></div>
            <div className="border-t pt-3 flex justify-between"><span className="text-slate-500">Total incasat</span><span className="font-bold text-lg text-emerald-600">{totalEur.toFixed(2)} EUR</span></div>
            {allServicesUsed.length > 0 && (
              <div className="border-t pt-3">
                <p className="text-sm text-slate-500 mb-2">Servicii efectuate</p>
                <div className="flex flex-wrap gap-1">{allServicesUsed.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Documents */}
      <Card className="bg-white">
        <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" />Documente client</CardTitle></CardHeader>
        <CardContent>
          <DocumentsSection documents={client.documents || []} uploadUrl={`/admin/clients/${clientId}/documents`} onRefresh={fetchClient} title="Documente" />
        </CardContent>
      </Card>

      {/* Vehicles */}
      <Card className="bg-white">
        <CardHeader><CardTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-blue-600" />Vehicule ({vehicles.length})</CardTitle></CardHeader>
        <CardContent>
          {vehicles.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">Niciun vehicul</p> : (
            <div className="space-y-3">
              {vehicles.map((v, i) => (
                <Link key={i} to={`/admin/vehicles/${v.vehicle_id}`} className="block p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{v.brand} {v.model}</p>
                      <p className="text-sm text-slate-500">VIN: {v.vin}</p>
                      <div className="flex gap-3 text-xs text-slate-400 mt-1">
                        {v.plate_number && <span>Nr: {v.plate_number}</span>}
                        {v.year && <span>An: {v.year}</span>}
                        {v.capacitate_cilindrica && <span>{v.capacitate_cilindrica} cc</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {v.plate_number && <Badge variant="secondary">{v.plate_number}</Badge>}
                      <Eye className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Orders */}
      <Card className="bg-white">
        <CardHeader><CardTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-blue-600" />Comenzi ({orders.length})</CardTitle></CardHeader>
        <CardContent>
          {orders.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">Nicio comanda</p> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Nr. Comanda</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Data</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Servicii</th>
                    <th className="text-right px-4 py-2 text-sm font-medium text-slate-600">Total</th>
                    <th className="text-center px-4 py-2 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-center px-4 py-2 text-sm font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const srvNames = [];
                    (o.vehicles || []).forEach(v => (v.services || []).forEach(s => {
                      const sid = typeof s === 'string' ? s : s.service_id;
                      const name = servicesMap[sid] || sid;
                      if (!srvNames.includes(name)) srvNames.push(name);
                    }));
                    return (
                      <tr key={o.order_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-sm">{o.order_number}</span>
                          {o.contract_data && <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300 text-xs">Contract</Badge>}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">{new Date(o.created_at).toLocaleDateString('ro-RO')}</td>
                        <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{srvNames.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}</div></td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">{o.total_price} EUR</td>
                        <td className="px-4 py-3 text-center"><Badge className={`${orderStatusColors[o.status] || 'bg-slate-100'} text-xs`}>{statusLabels[o.status] || o.status}</Badge></td>
                        <td className="px-4 py-3 text-center">
                          <Link to={`/admin/orders/${o.order_id}`}><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><Eye className="h-4 w-4" /></Button></Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card className="bg-white">
        <CardHeader><CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-blue-600" />Facturi ({invoices.length})</CardTitle></CardHeader>
        <CardContent>
          {invoices.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">Nicio factura</p> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Nr. Factura</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Data</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Scadenta</th>
                    <th className="text-right px-4 py-2 text-sm font-medium text-slate-600">Total EUR</th>
                    <th className="text-right px-4 py-2 text-sm font-medium text-slate-600">Total RON</th>
                    <th className="text-center px-4 py-2 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-center px-4 py-2 text-sm font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.invoice_id} className={`border-b border-slate-100 hover:bg-slate-50 ${inv.is_storno ? 'bg-red-50/50' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-sm">{inv.invoice_number}</span>
                        {inv.is_storno && <Badge variant="outline" className="ml-2 text-red-600 border-red-300 text-xs">STORNO</Badge>}
                        {inv.storno_invoice_id && <Badge variant="outline" className="ml-2 text-orange-600 border-orange-300 text-xs">STORNATA</Badge>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{inv.issue_date}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{inv.due_date}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">{inv.total} EUR</td>
                      <td className="px-4 py-3 text-sm text-right text-slate-500">{inv.total_ron > 0 ? `${inv.total_ron} RON` : '-'}</td>
                      <td className="px-4 py-3 text-center"><Badge className={`${invStatusColors[inv.status] || 'bg-slate-100'} text-xs`}>{invStatusLabels[inv.status] || inv.status}</Badge></td>
                      <td className="px-4 py-3 text-center">
                        <Link to={`/admin/invoices/${inv.invoice_id}`}><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><Eye className="h-4 w-4" /></Button></Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
